import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { type DragLocationHistory } from '@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types';
import {
	draggable,
	dropTargetForElements,
	monitorForElements,
	type ElementDragPayload
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import { attachClosestEdge, extractClosestEdge, type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { reorderWithEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge';
import { moveItemBetweenArrays } from '../../../utils/array/manipulation.utils';
import { type ProjectDetailsGroupFieldsFragment } from '../../__generated__/graphql-client-types';
import type {
	CardData,
	CardDropTargetData,
	MoveProductToGroupMutator,
	ProjectDetailsGroup,
	GroupData,
	ProjectDetailsGroupItem,
	ProjectDetailsPendingGroupItem,
	ProjectRootGroupItem,
	GroupEdgeType,
	MoveToGroupInputOptions
} from '../../types/project.types';
import { isHTMLElement } from '../../utils/dom/dom.utils';

const GROUP_HEADER_HEIGHT = '59px';
const TOOLBAR_HEIGHT = 79;

export const generateDragPreview = (element: HTMLDivElement, customHeight?: string): HTMLElement | null => {
	const rect = element.getBoundingClientRect();
	const preview = element.cloneNode(true);
	if (!isHTMLElement(preview)) {
		return null;
	}
	preview.style.width = `${rect.width}px`;
	preview.style.height = customHeight ?? `${rect.height}px`;

	// truncate product title to fit in the preview
	const productTitle: HTMLDivElement | null = preview.querySelector('#product-title');
	if (productTitle) {
		productTitle.style.maxWidth = '500px';
	}

	return preview;
};

export const shouldHideDropIndicator = (closestEdge: Edge, currentIndex: number, sourceIndex: number): boolean => {
	const isItemBeforeSource = currentIndex === sourceIndex - 1;
	const isItemAfterSource = currentIndex === sourceIndex + 1;
	return (isItemBeforeSource && closestEdge === 'bottom') || (isItemAfterSource && closestEdge === 'top');
};

const isGroupData = (data: Record<string | symbol, unknown>): data is GroupData => {
	return data.type === 'GROUP';
};

const isCardDropTargetData = (data: Record<string | symbol, unknown>): data is CardDropTargetData => {
	return data.type === 'CARD_DROP_TARGET';
};

const isCardData = (data: Record<string | symbol, unknown>): data is CardData => {
	return data.type === 'CARD';
};

export const isValidMove = (homeIndex: number, destinationIndex: number): boolean => {
	if (homeIndex === -1 || destinationIndex === -1) {
		return false;
	}
	if (homeIndex === destinationIndex) {
		return false;
	}
	return true;
};

export const modifyPendingItems = (
	groups: ProjectDetailsGroupFieldsFragment[],
	groupIndex: number,
	newPendingItems: ProjectDetailsPendingGroupItem[]
): ProjectDetailsGroupFieldsFragment[] => {
	const oldGroup = groups[groupIndex];
	const newGroup: ProjectDetailsGroupFieldsFragment = {
		...oldGroup,
		pending: newPendingItems
	};
	groups[groupIndex] = newGroup;
	return groups;
};

type OnDropGroupArgs = {
	source: ElementDragPayload;
	location: DragLocationHistory;
	projectGroups: ProjectDetailsGroupFieldsFragment[];
	onDrop: () => void;
	updateGroupState: (projectGroups: ProjectDetailsGroupFieldsFragment[]) => void;
	moveGroupMutation: (groupId: number, ordinal: number) => Promise<void>;
	disableDrag: (isDisabled: boolean) => void;
};

export const onDropGroup = async ({
	source,
	location,
	projectGroups,
	onDrop,
	updateGroupState,
	moveGroupMutation,
	disableDrag
}: OnDropGroupArgs) => {
	const dragging = source.data;
	if (!isGroupData(dragging)) {
		return;
	}

	const target = location.current.dropTargets[0];
	if (!target) {
		return;
	}

	const dropTargetData = target.data;
	if (!isGroupData(dropTargetData)) {
		return;
	}

	const homeIndex = projectGroups.findIndex((group) => group.id === dragging.group.id);
	const destinationIndex = projectGroups.findIndex((group) => group.id === dropTargetData.group.id);
	if (!isValidMove(homeIndex, destinationIndex)) {
		return;
	}

	disableDrag(true);

	const reordered = reorder({
		list: projectGroups,
		startIndex: homeIndex,
		finishIndex: destinationIndex
	});

	updateGroupState(reordered);

	try {
		await moveGroupMutation(projectGroups[homeIndex].id, destinationIndex + 1);
	} finally {
		disableDrag(false);
		onDrop();
	}
};

type MoveCardCommonArgs = {
	homeGroup: { __typename?: 'ProjectGroup' } & ProjectDetailsGroupFieldsFragment;
	homeGroupIndex: number;
	cardIndexInHome: number;
	groupsCopy: ProjectDetailsGroupFieldsFragment[];
	disableDrag: (isDisabled: boolean) => void;
};

type ReorderCardInHomeArgs = MoveCardCommonArgs & {
	closestEdge: Edge | null;
	dropTargetData: CardDropTargetData;
	reorderProductMutation: (groupId: number, targetOrdinal: number, itemIds: number[]) => Promise<void>;
};

export const reorderInHomeGroup = ({
	homeGroup,
	dropTargetData,
	homeGroupIndex,
	cardIndexInHome,
	closestEdge,
	groupsCopy,
	disableDrag,
	reorderProductMutation
}: ReorderCardInHomeArgs): ProjectDetailsGroupFieldsFragment[] => {
	const cardFinishIndex = homeGroup.pending?.findIndex((card) => card.id === dropTargetData.card.id) ?? -1;
	if (!isValidMove(cardIndexInHome, cardFinishIndex)) {
		return groupsCopy;
	}

	const productToMove = homeGroup.pending?.[cardIndexInHome];
	if (!productToMove) {
		return groupsCopy;
	}

	const reordered = reorderWithEdge({
		axis: 'vertical',
		list: homeGroup.pending ?? [],
		startIndex: cardIndexInHome,
		indexOfTarget: cardFinishIndex,
		closestEdgeOfTarget: closestEdge
	});

	// reorderWithEdge produces a no-op when the drop indicator is hidden (adjacent card,
	// same-direction edge). Firing the mutation in that case sends the wrong ordinal to the
	// server and causes the card to jump to an unexpected position, so bail out early.
	const cardMovedIndex = reordered.findIndex((card) => card.id === productToMove.id);
	if (cardMovedIndex === cardIndexInHome) {
		return groupsCopy;
	}

	disableDrag(true);

	groupsCopy = modifyPendingItems(groupsCopy, homeGroupIndex, reordered);

	void reorderProductMutation(homeGroup.id, cardFinishIndex, [productToMove.id]).finally(() => {
		disableDrag(false);
	});

	return groupsCopy;
};

type OnFinishCardDropArgs = MoveCardCommonArgs & {
	finalIndex: number;
	destinationGroup: ProjectDetailsGroupFieldsFragment;
	destinationGroupIndex: number;
	productToMove: ProjectDetailsGroupItem;
	moveProductToAnotherGroup: MoveProductToGroupMutator;
};

export const onFinishCardDrop = ({
	homeGroup,
	cardIndexInHome,
	finalIndex,
	destinationGroup,
	moveProductToAnotherGroup,
	disableDrag,
	productToMove,
	groupsCopy,
	homeGroupIndex,
	destinationGroupIndex
}: OnFinishCardDropArgs): ProjectDetailsGroupFieldsFragment[] => {
	const [sourceCards, destinationCards] = moveItemBetweenArrays(
		homeGroup.pending ?? [],
		destinationGroup.pending ?? [],
		cardIndexInHome,
		finalIndex
	);

	groupsCopy = modifyPendingItems(groupsCopy, homeGroupIndex, sourceCards);
	groupsCopy = modifyPendingItems(groupsCopy, destinationGroupIndex, destinationCards);

	void moveProductToAnotherGroup({
		sourceGroupId: homeGroup.id,
		sourceProductId: productToMove.id,
		targetOrdinal: finalIndex,
		targetGroupId: destinationGroup.id
	}).finally(() => {
		disableDrag(false);
	});

	return groupsCopy;
};

type MoveCardToAnotherGroupArgs = MoveCardCommonArgs & {
	closestEdge: Edge | null;
	destinationGroup: { __typename?: 'ProjectGroup' } & ProjectDetailsGroupFieldsFragment;
	destinationGroupIndex: number;
	dropTargetData: CardDropTargetData;
	moveProductToAnotherGroup: MoveProductToGroupMutator;
};

export const moveCardToAnotherGroup = ({
	homeGroup,
	destinationGroup,
	cardIndexInHome,
	closestEdge,
	dropTargetData,
	groupsCopy,
	disableDrag,
	...rest
}: MoveCardToAnotherGroupArgs): ProjectDetailsGroupFieldsFragment[] => {
	if (!destinationGroup) {
		return groupsCopy;
	}

	const productToMove = homeGroup.pending?.[cardIndexInHome];
	if (!productToMove) {
		return groupsCopy;
	}

	disableDrag(true);

	const indexOfTarget = destinationGroup.pending?.findIndex((card) => card.id === dropTargetData.card.id) ?? -1;
	const finalIndex = closestEdge === 'bottom' ? indexOfTarget + 1 : indexOfTarget;

	return onFinishCardDrop({
		homeGroup,
		cardIndexInHome,
		finalIndex,
		destinationGroup,
		groupsCopy,
		productToMove,
		disableDrag,
		...rest
	});
};
type MoveCardToGroupEdgesArgs = MoveCardCommonArgs & {
	dropTargetData: GroupData;
	projectGroups: ProjectDetailsGroupFieldsFragment[];
	moveProductToAnotherGroup: MoveProductToGroupMutator;
	reorderProductMutation: (groupId: number, targetOrdinal: number, itemIds: number[]) => Promise<void>;
};

export const moveCardToGroupEdges = ({
	homeGroup,
	dropTargetData,
	cardIndexInHome,
	homeGroupIndex,
	projectGroups,
	groupsCopy,
	disableDrag,
	moveProductToAnotherGroup,
	reorderProductMutation
}: MoveCardToGroupEdgesArgs): ProjectDetailsGroupFieldsFragment[] => {
	const destinationGroupIndex = projectGroups.findIndex((group) => group.id === dropTargetData.group.id);
	const destinationGroup = projectGroups[destinationGroupIndex];
	if (!destinationGroup) {
		return groupsCopy;
	}

	const productToMove = homeGroup.pending?.[cardIndexInHome];
	if (!productToMove) {
		return groupsCopy;
	}

	disableDrag(true);

	// dropping on home group
	if (homeGroup.id === destinationGroup.id) {
		// move to last position
		const cardFinishIndex = (homeGroup.pending?.length ?? 1) - 1;

		const reordered = reorder({
			list: homeGroup.pending ?? [],
			startIndex: cardIndexInHome,
			finishIndex: cardFinishIndex
		});

		groupsCopy = modifyPendingItems(groupsCopy, homeGroupIndex, reordered);

		void reorderProductMutation(homeGroup.id, cardFinishIndex, [productToMove.id]).finally(() => {
			disableDrag(false);
		});

		return groupsCopy;
	}

	// dropping onto a different group, but not on another product card
	const destinationPendingItems = destinationGroup.pending ?? [];

	return onFinishCardDrop({
		homeGroup,
		cardIndexInHome,
		destinationGroup: destinationGroup ?? [],
		finalIndex: destinationPendingItems.length,
		groupsCopy,
		productToMove,
		homeGroupIndex,
		destinationGroupIndex,
		moveProductToAnotherGroup,
		disableDrag
	});
};

type OnDropCardArgs = {
	source: ElementDragPayload;
	location: DragLocationHistory;
	projectGroups: ProjectDetailsGroupFieldsFragment[];
	updateGroupState: (projectGroups: ProjectDetailsGroupFieldsFragment[]) => void;
	reorderProductMutation: (groupId: number, targetOrdinal: number, itemIds: number[]) => Promise<void>;
	moveProductToAnotherGroup: MoveProductToGroupMutator;
	disableDrag: (isDisabled: boolean) => void;
};

export const onDropCard = ({
	source,
	location,
	projectGroups,
	updateGroupState,
	reorderProductMutation,
	moveProductToAnotherGroup,
	disableDrag
}: OnDropCardArgs) => {
	const dragging = source.data;
	if (!isCardData(dragging)) {
		return;
	}

	const target = location.current.dropTargets[0];
	if (!target) {
		return;
	}

	const groupsCopy = [...projectGroups];
	const dropTargetData = target.data;
	const closestEdge = extractClosestEdge(dropTargetData);
	const homeGroupIndex = projectGroups.findIndex((group) => group.id === dragging.groupId);
	const homeGroup: ProjectDetailsGroup | undefined = projectGroups[homeGroupIndex];
	if (!homeGroup) {
		return;
	}

	const cardIndexInHome = homeGroup.pending?.findIndex((card) => card.id === dragging.card.id) ?? -1;
	const commonArgs: MoveCardCommonArgs = {
		homeGroup,
		homeGroupIndex,
		cardIndexInHome,
		groupsCopy,
		disableDrag
	};
	let newGroupState = groupsCopy;

	// dropping on another product card
	if (isCardDropTargetData(dropTargetData)) {
		const destinationGroupIndex = projectGroups.findIndex((group) => group.id === dropTargetData.groupId);
		const destinationGroup = projectGroups[destinationGroupIndex];

		const dropOnCardArgs: MoveCardCommonArgs & {
			dropTargetData: CardDropTargetData;
			closestEdge: Edge | null;
		} = {
			...commonArgs,
			closestEdge,
			dropTargetData,
			groupsCopy
		};

		if (homeGroup === destinationGroup) {
			newGroupState = reorderInHomeGroup({
				...dropOnCardArgs,
				reorderProductMutation
			});
		} else {
			newGroupState = moveCardToAnotherGroup({
				...dropOnCardArgs,
				destinationGroup,
				destinationGroupIndex,
				moveProductToAnotherGroup
			});
		}
	} else if (isGroupData(dropTargetData)) {
		// dropping onto a group, but not onto a card ie. "shop from projects" section
		newGroupState = moveCardToGroupEdges({
			...commonArgs,
			dropTargetData,
			projectGroups,
			moveProductToAnotherGroup,
			reorderProductMutation
		});
	}

	updateGroupState(newGroupState);
};

type MonitorGroupsArgs = {
	projectGroups: ProjectDetailsGroupFieldsFragment[];
	onDrop: () => void;
	updateGroupState: (projectGroups: ProjectDetailsGroupFieldsFragment[]) => void;
	moveGroupMutation: (groupId: number, ordinal: number) => Promise<void>;
	disableDrag: (isDisabled: boolean) => void;
};

export const monitorGroups = ({ projectGroups, onDrop, updateGroupState, moveGroupMutation, disableDrag }: MonitorGroupsArgs) => ({
	canMonitor: ({ source }) => isGroupData(source.data),
	onDrop: async ({ source, location }) => {
		const dropTarget = location?.current?.dropTargets[0]?.element;
		if (dropTarget) {
			dropTarget.scrollIntoView();
			setTimeout(() => {
				window.scrollBy({ top: -TOOLBAR_HEIGHT, behavior: 'smooth' });
			}, 100);
		}
		await onDropGroup({ source, location, projectGroups, onDrop, updateGroupState, moveGroupMutation, disableDrag });
	}
});

type MonitorCardsArgs = {
	projectGroups: ProjectDetailsGroupFieldsFragment[];
	onDragStart: (productId: number) => void;
	updateGroupState: (projectGroups: ProjectDetailsGroupFieldsFragment[]) => void;
	reorderProductMutation: (groupId: number, targetOrdinal: number, itemIds: number[]) => Promise<void>;
	moveProductToAnotherGroup: (moveProductInput: MoveToGroupInputOptions) => Promise<void>;
	disableDrag: (isDisabled: boolean) => void;
};

export const monitorCards = ({
	projectGroups,
	onDragStart,
	updateGroupState,
	reorderProductMutation,
	moveProductToAnotherGroup,
	disableDrag
}: MonitorCardsArgs) => ({
	canMonitor: ({ source }) => isCardData(source.data),
	onDragStart: ({ source }) => {
		if (!isCardData(source.data)) {
			return;
		}
		onDragStart(source.data.card.id);
	},
	onDrop: ({ source, location }) => {
		onDropCard({
			source,
			location,
			projectGroups,
			updateGroupState,
			reorderProductMutation,
			moveProductToAnotherGroup,
			disableDrag
		});
	}
});

type AttachDragAndDropArgs = {
	projectGroups: ProjectDetailsGroupFieldsFragment[];
	onDragStart: (productId: number) => void;
	onDrop: () => void;
	// Updates UI before mutation completes
	updateGroupState: (projectGroups: ProjectDetailsGroupFieldsFragment[]) => void;
	moveGroupMutation: (groupId: number, ordinal: number) => Promise<void>;
	reorderProductMutation: (groupId: number, targetOrdinal: number, itemIds: number[]) => Promise<void>;
	moveProductToAnotherGroup: MoveProductToGroupMutator;
	// Prevents dragging until previous operation completes
	disableDrag: (isDisabled: boolean) => void;
};

export const attachDragAndDrop = ({
	projectGroups,
	onDragStart,
	onDrop,
	updateGroupState,
	moveGroupMutation,
	reorderProductMutation,
	moveProductToAnotherGroup,
	disableDrag
}: AttachDragAndDropArgs) => {
	return combine(
		monitorForElements(monitorGroups({ projectGroups, onDrop, updateGroupState, moveGroupMutation, disableDrag })),
		monitorForElements(
			monitorCards({ projectGroups, onDragStart, updateGroupState, reorderProductMutation, moveProductToAnotherGroup, disableDrag })
		)
	);
};

export const initDraggableGroups = (
	groupElement: HTMLDivElement,
	isDragDisabled: boolean,
	data: GroupData,
	updateDragState: (isDragging: boolean) => void
) => ({
	canDrag: () => !isDragDisabled,
	getInitialData: () => data,
	onDragStart: () => updateDragState(true),
	onDrop: () => updateDragState(false),
	onGenerateDragPreview: ({ nativeSetDragImage }) => {
		setCustomNativeDragPreview({
			nativeSetDragImage,
			render({ container }) {
				const preview = generateDragPreview(groupElement, GROUP_HEADER_HEIGHT);
				if (preview) {
					container.appendChild(preview);
				}
			}
		});
	}
});

export const initGroupDropTargets = (
	data: GroupData,
	groupElement: HTMLDivElement,
	index: number,
	updateEdge: (edge: Edge | null, type: GroupEdgeType) => void
) => ({
	getData: ({ input, source }) => {
		// For group drags use direction-based edge detection so the indicator position
		// tracks whether the source is above or below this group, not the cursor midpoint.
		if (source && source.data && isGroupData(source.data)) {
			const edge: Edge = source.data.index < index ? 'bottom' : 'top';
			return attachClosestEdge(data, { element: groupElement, input, allowedEdges: [edge] });
		}
		return attachClosestEdge(data, { element: groupElement, input, allowedEdges: ['top', 'bottom'] });
	},
	onDrag: ({ source }) => {
		if (!isGroupData(source.data)) {
			return;
		}

		const isSource = source.element === groupElement;
		if (isSource) {
			updateEdge(null, null);
			return;
		}

		updateEdge(source.data.index < index ? 'bottom' : 'top', 'GROUP');
	},
	onDragEnter: ({ self, source }) => {
		const group = self.data.group;
		const isGroupEmpty = group.pending.length === 0 && group.purchased.length === 0;
		if (isGroupEmpty && source.data.type === 'CARD') {
			updateEdge('top', 'CARD');
		}
	},
	onDragLeave: () => {
		updateEdge(null, null);
	},
	onDrop: () => {
		updateEdge(null, null);
	}
});

type MakeGroupDropTargetsArgs = {
	element: HTMLDivElement;
	dragHandle: HTMLDivElement;
	data: GroupData;
	index: number;
	isDragDisabled: boolean;
	updateDragState: (isDragging: boolean) => void;
	updateEdge: (edge: Edge | null, type: GroupEdgeType) => void;
};

export const makeGroupDropTargets = ({
	element,
	dragHandle,
	data,
	index,
	isDragDisabled,
	updateDragState,
	updateEdge
}: MakeGroupDropTargetsArgs) => {
	return combine(
		draggable({
			element: dragHandle,
			...initDraggableGroups(element, isDragDisabled, data, updateDragState)
		}),
		dropTargetForElements({
			element,
			...initGroupDropTargets(data, element, index, updateEdge)
		})
	);
};

export const initDraggableCards = (
	cardElement: HTMLDivElement,
	card: ProjectRootGroupItem,
	groupId: number,
	index: number,
	isDragDisabled: boolean,
	updateDragState: (isDragging: boolean) => void
) => ({
	canDrag: () => !isDragDisabled,
	getInitialData: ({ element }) => ({ type: 'CARD', card, groupId, index, rect: element.getBoundingClientRect() }),
	onDragStart: () => updateDragState(true),
	onDrop: () => updateDragState(false),
	onGenerateDragPreview: ({ nativeSetDragImage }) => {
		setCustomNativeDragPreview({
			nativeSetDragImage,
			render({ container }) {
				const preview = generateDragPreview(cardElement);
				if (preview) {
					container.appendChild(preview);
				}
			}
		});
	}
});

export const initCardDropTargets = (
	card: ProjectRootGroupItem,
	cardElement: HTMLDivElement,
	groupId: number,
	index: number,
	updateEdge: (edge: Edge | null) => void
) => ({
	canDrop: ({ source }) => isCardData(source.data),
	getData: ({ element, input, source }) => {
		const data = { type: 'CARD_DROP_TARGET', card, groupId };
		// For same-group drags use direction-based edge detection: the indicator tracks whether
		// the source is above or below this card, not the cursor's position within the card.
		// This ensures the drop always lands in the direction the user is dragging.
		if (source && source.data && isCardData(source.data) && source.data.groupId === groupId) {
			const edge: Edge = source.data.index < index ? 'bottom' : 'top';
			return attachClosestEdge(data, { element, input, allowedEdges: [edge] });
		}
		return attachClosestEdge(data, { element, input, allowedEdges: ['top', 'bottom'] });
	},
	onDragEnter({ source, self }) {
		if (!isCardData(source.data)) {
			return;
		}
		if (source.data.card.id === card.id) {
			return;
		}

		if (source.data.groupId === groupId) {
			updateEdge(source.data.index < index ? 'bottom' : 'top');
			return;
		}

		const closestEdge = extractClosestEdge(self.data);
		if (!closestEdge) {
			return;
		}
		updateEdge(closestEdge);
	},
	onDrag({ source, self }) {
		const isSource = source.element === cardElement;
		if (isSource) {
			updateEdge(null);
			return;
		}

		if (!isCardData(source.data)) {
			return;
		}
		if (source.data.card.id === card.id) {
			return;
		}

		if (source.data.groupId === groupId) {
			updateEdge(source.data.index < index ? 'bottom' : 'top');
			return;
		}

		const extractedClosestEdge = extractClosestEdge(self.data);
		if (!extractedClosestEdge) {
			return;
		}

		const sourceIndex = source.data.index;

		if (shouldHideDropIndicator(extractedClosestEdge, index, sourceIndex)) {
			updateEdge(null);
			return;
		}

		updateEdge(extractedClosestEdge);
	},
	onDragLeave() {
		updateEdge(null);
	},
	onDrop() {
		updateEdge(null);
	}
});

type MakeCardDropTargetsArgs = {
	cardElement: HTMLDivElement;
	dragHandle: HTMLDivElement;
	// Makes the product image a drag handle
	productImage: HTMLDivElement;
	card: ProjectRootGroupItem;
	groupId: number;
	index: number;
	isDragDisabled: boolean;
	updateDragState: (isDragging: boolean) => void;
	updateEdge: (edge: Edge | null) => void;
};

export const makeCardDropTargets = ({
	cardElement,
	dragHandle,
	productImage,
	card,
	groupId,
	index,
	isDragDisabled,
	updateDragState,
	updateEdge
}: MakeCardDropTargetsArgs) => {
	return combine(
		draggable({
			element: dragHandle,
			...initDraggableCards(cardElement, card, groupId, index, isDragDisabled, updateDragState)
		}),
		draggable({
			element: productImage,
			...initDraggableCards(cardElement, card, groupId, index, isDragDisabled, updateDragState)
		}),
		dropTargetForElements({
			element: cardElement,
			...initCardDropTargets(card, cardElement, groupId, index, updateEdge)
		})
	);
};
